interface Props {
  lang: string;
}

const t = (lang: string, en: string, es: string) => (lang === 'en' ? en : es);

// ─── Reusable primitives ──────────────────────────────────────────────────────

interface BoxProps {
  service: string;
  label: string;
  description?: string;
  accent?: boolean;
  dim?: boolean;
  className?: string;
}

function ServiceBox({ service, label, description, accent = false, dim = false, className = '' }: BoxProps) {
  return (
    <div
      className={[
        'rounded border bg-bg-surface px-3 py-2 text-center',
        accent ? 'border-accent' : 'border-border',
        dim ? 'opacity-70' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <p className="font-mono text-xs text-accent">{service}</p>
      <p className="mt-0.5 font-mono text-xs font-semibold text-text-primary">{label}</p>
      {description && (
        <p className="mt-1 font-mono text-[10px] leading-tight text-text-muted">{description}</p>
      )}
    </div>
  );
}

function Arrow({ label, vertical = false }: { label?: string; vertical?: boolean }) {
  if (vertical) {
    return (
      <div className="flex flex-col items-center py-1">
        <div className="h-4 w-px bg-border" />
        <span className="font-mono text-[10px] text-text-muted">▼</span>
        {label && <span className="font-mono text-[10px] text-text-muted">{label}</span>}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 px-1">
      {label && <span className="font-mono text-[10px] text-text-muted">{label}</span>}
      <span className="font-mono text-xs text-text-muted">→</span>
    </div>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
      {children}
    </p>
  );
}

function Badge({ children, color = 'muted' }: { children: string; color?: 'muted' | 'accent' }) {
  return (
    <span
      className={[
        'rounded border px-1.5 py-0.5 font-mono text-[10px]',
        color === 'accent'
          ? 'border-accent text-accent'
          : 'border-border text-text-muted',
      ].join(' ')}
    >
      {children}
    </span>
  );
}

// ─── Sub-sections ─────────────────────────────────────────────────────────────

function UserToCloudFrontRow({ lang }: { lang: string }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <ServiceBox
        service="Internet"
        label={t(lang, 'Users', 'Usuarios')}
        accent={false}
      />
      <Arrow />
      <ServiceBox
        service="Amazon CloudFront"
        label="CDN / Cache"
        description={t(lang, 'Global edge network', 'Red de edge global')}
        accent
      />
      <Arrow />
      <ServiceBox
        service="Amazon S3"
        label="React SPA"
        description={t(lang, 'Static hosting', 'Hosting estático')}
        accent
      />
    </div>
  );
}

function LambdaGroup({
  functions,
  dbLabel,
  dbDescription,
  extra,
}: {
  functions: string[];
  dbLabel: string;
  dbDescription: string;
  extra?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      {/* Lambda functions */}
      <div className="flex flex-wrap justify-center gap-2">
        {functions.map((fn) => (
          <ServiceBox key={fn} service="λ Lambda" label={fn} accent />
        ))}
      </div>
      <Arrow vertical label="DynamoDB SDK" />
      <ServiceBox
        service="Amazon DynamoDB"
        label={dbLabel}
        description={dbDescription}
        accent
      />
      {extra}
    </div>
  );
}

function PortfolioApiSection({ lang }: { lang: string }) {
  return (
    <div className="rounded border border-border bg-bg-primary p-4">
      <SectionLabel>{t(lang, 'Portfolio API', 'API Portfolio')}</SectionLabel>
      <div className="flex flex-col items-center gap-2">
        <ServiceBox
          service="Amazon API Gateway"
          label="Portfolio API"
          description="REST · CORS"
          accent
        />
        <Arrow vertical />
        <LambdaGroup
          functions={['get-items', 'submit-contact', 'health']}
          dbLabel="portfolio"
          dbDescription={t(lang, 'GSI on itemType', 'GSI en itemType')}
        />
      </div>
    </div>
  );
}

function AiChatApiSection({ lang }: { lang: string }) {
  return (
    <div className="rounded border border-border bg-bg-primary p-4">
      <SectionLabel>{t(lang, 'AI Chat API', 'API de Chat IA')}</SectionLabel>
      <div className="flex flex-col items-center gap-2">
        <ServiceBox
          service="Amazon API Gateway"
          label="AI Chat API"
          description="REST · CORS"
          accent
        />
        <Arrow vertical />
        <LambdaGroup
          functions={['chat', 'health']}
          dbLabel="ai-chat"
          dbDescription={t(lang, 'TTL on messages', 'TTL en mensajes')}
          extra={
            <>
              <Arrow vertical label="InvokeModel" />
              <ServiceBox
                service="AWS Bedrock"
                label="Claude Haiku"
                description={t(lang, 'Foundation model', 'Modelo fundacional')}
                accent
              />
            </>
          }
        />
      </div>
    </div>
  );
}

function ObservabilityBar({ lang }: { lang: string }) {
  return (
    <div className="rounded border border-border bg-bg-primary px-4 py-3">
      <SectionLabel>{t(lang, 'Observability', 'Observabilidad')}</SectionLabel>
      <div className="flex flex-wrap justify-center gap-3">
        <div className="flex items-center gap-2">
          <ServiceBox
            service="AWS X-Ray"
            label={t(lang, 'Tracing', 'Trazabilidad')}
            description={t(lang, 'All Lambdas', 'Todas las Lambdas')}
            dim
          />
        </div>
        <div className="flex items-center gap-2">
          <ServiceBox
            service="CloudWatch"
            label={t(lang, 'Alarms', 'Alarmas')}
            description={t(lang, 'AI Chat errors', 'Errores de Chat IA')}
            dim
          />
          <Arrow label="→" />
          <ServiceBox
            service="Amazon SNS"
            label={t(lang, 'Notifications', 'Notificaciones')}
            dim
          />
        </div>
      </div>
    </div>
  );
}

function Legend({ lang }: { lang: string }) {
  return (
    <div className="flex flex-wrap justify-center gap-4">
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-sm border border-accent bg-bg-surface" />
        <span className="font-mono text-[10px] text-text-muted">
          {t(lang, 'Key service', 'Servicio clave')}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-sm border border-border bg-bg-surface opacity-70" />
        <span className="font-mono text-[10px] text-text-muted">
          {t(lang, 'Supporting service', 'Servicio de soporte')}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Badge color="accent">CORS</Badge>
        <span className="font-mono text-[10px] text-text-muted">
          {t(lang, 'on both API Gateways', 'en ambas API Gateways')}
        </span>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ArchitectureDiagram({ lang }: Props) {
  const effectiveLang = lang === 'en' ? 'en' : 'es';

  return (
    <div className="w-full space-y-6 rounded border border-border bg-bg-primary p-4 md:p-6">
      {/* Title */}
      <div className="text-center">
        <p className="font-mono text-xs text-accent">
          {t(effectiveLang, '// architecture', '// arquitectura')}
        </p>
        <p className="mt-1 font-mono text-[10px] text-text-muted">
          {t(
            effectiveLang,
            'AWS infrastructure — all services in a single region',
            'Infraestructura AWS — todos los servicios en una sola región',
          )}
        </p>
      </div>

      {/* Layer 1: Users → CloudFront → S3 */}
      <div className="space-y-1">
        <SectionLabel>
          {t(effectiveLang, 'Frontend delivery', 'Entrega del frontend')}
        </SectionLabel>
        <UserToCloudFrontRow lang={effectiveLang} />
      </div>

      {/* Connector from CloudFront down to APIs */}
      <div className="flex justify-center">
        <div className="flex flex-col items-center">
          <div className="h-4 w-px bg-border" />
          <span className="font-mono text-[10px] text-text-muted">▼</span>
          <span className="font-mono text-[10px] text-text-muted">
            {t(effectiveLang, 'API calls (fetch)', 'Llamadas API (fetch)')}
          </span>
        </div>
      </div>

      {/* Layer 2: Both APIs side by side on md+, stacked on mobile */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <PortfolioApiSection lang={effectiveLang} />
        <AiChatApiSection lang={effectiveLang} />
      </div>

      {/* Layer 3: Observability */}
      <ObservabilityBar lang={effectiveLang} />

      {/* Legend */}
      <Legend lang={effectiveLang} />
    </div>
  );
}
